import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {ComponentContainer} from "../../../lib/template/ComponentContainer";
import {LazyFetchingService} from "../../../lib/services/lazy-fetching-service";
import {AdvancePaymentResponse, AdvancePaymentSubtotal} from "../../../lib/types/types";
import useSWR, {mutate} from "swr";
import {ReactiveTable} from "react-ive-tables";
import {IDropdownOption} from "@xal3xfx/react-utils/dist/util-service";
import moment from "moment";
import {ExportExcelParams} from "react-ive-tables/dist/ReactiveTable";
import {formatNumber, getFileNameFromDisposition, parseDateFilter} from "../../../lib/common";
import CreateAdvancePayment from "./CreateAdvancePayment";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {useToast} from "../../../lib/hooks/useToast";
import {AdvancePaymentService} from "../../../lib/services/advance-payment-service";
import CreateCreditNoteForAdvancePayment from "./CreateCreditNoteForAdvancePayment";
import {Tooltip} from "primereact/tooltip";
import {Badge} from "primereact/badge";
import {Dialog} from "primereact/dialog";
import {Files} from "../../../lib/template/Files";
import {InvoiceService} from "../../../lib/services/invoice-service";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {Column} from "primereact/column";
import FileSaver from "file-saver";

interface Props {

}

//It is very important this service to stay out of the component so that it is created only once and preserves state.
const lazyFetchingService = new LazyFetchingService<AdvancePaymentResponse, AdvancePaymentSubtotal>('/advance-payments/lazy');


const AdvancePayments: React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const didMountRef = useRef(false);
    const toastHelpers = useToast();
    const advancePaymentsService = new AdvancePaymentService(f, toastHelpers);
    const invoiceService = new InvoiceService(f, toastHelpers);

    const [selectedAdvancePayment, setSelectedAdvancePayment] = useState<AdvancePaymentResponse>();
    const [showCreateCreditNoteDialog, setShowCreateCreditNoteDialog] = useState(false);
    const [showFilesDialog, setShowFilesDialog] = useState(false);

    const fetchData = lazyFetchingService.getDataFetcher;
    const fetcher = lazyFetchingService.getLazyFetcher;

    const {data: advancePaymentsTableData} = useSWR<Awaited<ReturnType<typeof fetcher>>, Error>(`/advance-payments/lazy`, {fetcher: fetcher});
    const {data: clientOptions} = useSWR<IDropdownOption[], Error>('/clients/dropdown');

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
        }
    }, []);

    const getColumnTemplate = () => {
        return {
            clientId: (rowData: AdvancePaymentResponse) => clientOptions?.find(el => el.id === rowData.clientId)?.description,
            amount: (rowData: AdvancePaymentResponse) => formatNumber(rowData.amount),
            remainingAmount: (rowData: AdvancePaymentResponse) => {
                const remainingAmountFormatted = formatNumber(rowData.remainingAmount)
                if (rowData.creditNoticeNote) {
                    return <div>
                        <span className={"credit-notice-note bg-red-400 p-2 border-round text-white"}
                              data-pr-tooltip={f({id: "creditNoticeNote"})}
                              data-pr-position="right">{rowData.creditNoticeNote}</span>
                        <Tooltip target=".credit-notice-note"/>
                    </div>
                }
                return remainingAmountFormatted;
            },
            operations: (rowData: AdvancePaymentResponse) => <div className={"flex justify-content-center gap-1"}>
                <Button icon={'pi pi-file-export'} className={'p-button-danger mr-2'}
                        disabled={rowData.creditNoticeNote !== null}
                        tooltip={f({id: 'creditNote'})}
                        tooltipOptions={{position: 'top'}}
                        onClick={() => {
                            setSelectedAdvancePayment(rowData);
                            setShowCreateCreditNoteDialog(true)
                        }}/>
                <Button icon={'pi pi-file'} className={'mr-2 p-button-info'} tooltip={f({id: 'files'})}
                        tooltipOptions={{position: 'top'}}
                        onClick={() => {
                            setSelectedAdvancePayment(rowData);
                            setShowFilesDialog(true)
                        }}>
                    {rowData.filesCount > 0 &&
                        <Badge severity={'warning'} style={{position: 'absolute', right: 0, top: 0}}
                               value={rowData.filesCount}/>}
                </Button>
            </div>
        }
    }

    const headerButtons = [
        {
            onClick: () => document.querySelector("form")!.scrollIntoView({behavior: "smooth", block: "start"}),
            icon: 'pi pi-plus',
            className: 'p-button-success ml-2'
        }
    ]

    const exportExcel = (params: ExportExcelParams) => {
        //Reverse date filter
        if (params.filters && params.filters['date']) {
            params.filters['date'] = parseDateFilter(params.filters['date']);
        }


        const parsedSortOrder = params.sort?.reduce((acc: any, el: any) => {
            return {...acc, [el.field]: 'ascending'}
        }, {})
        document.body.style.cursor = "wait";
        //Only order is needed (sort param is sent but not needed)


        advancePaymentsService.exportExcel(params.filters).then(res => {
            const blob = new Blob([res.data], {type: res.headers['content-type']});
            const contentDisposition = res.headers['content-disposition'];
            const decodedFileName = getFileNameFromDisposition(contentDisposition);

            FileSaver.saveAs(blob, decodedFileName);
        }).finally(() => document.body.style.cursor = "auto")

    }

    const getFooterGroup = () => {
        const totalFormatted = formatNumber(advancePaymentsTableData?.subtotals?.amount || 0);
        const remainingFormatted = formatNumber(advancePaymentsTableData?.subtotals?.remainingAmount || 0);


        return <ColumnGroup>
            <Row>
                <Column colSpan={1} footer={f({id: "total"}) + ":"}
                        footerStyle={{textAlign: 'center'}}/>
                <Column footer={totalFormatted}
                        footerStyle={{textAlign: 'center', fontWeight: 'bold'}}/>
                <Column
                    footer={remainingFormatted}
                    footerStyle={{textAlign: 'center', fontWeight: 'bold'}}/>
                <Column colSpan={1}/>
            </Row>
        </ColumnGroup>;
    }

    const getSpecialFilters = () => {
        return {
            clientId: (options: any) => <Dropdown filter={true} showClear value={options.value}
                                                  filterInputAutoFocus={true}
                                                  resetFilterOnHide
                                                  options={clientOptions}
                                                  optionValue={'id'} optionLabel={'description'}
                                                  placeholder={f({id: 'chooseLabel'})}
                                                  onChange={(e) => options.filterApplyCallback(e.value)}
                                                  style={{textAlign: "left"}}/>,
        }
    }

    const closeDialog = async () => {
        await mutate("/advance-payments/lazy");
        setShowCreateCreditNoteDialog(false);
    }

    const handleCreateCreditNote = (note?: string, date?: Date | undefined | null) => {
        if (!selectedAdvancePayment || !selectedAdvancePayment.id) return;
        advancePaymentsService.createCreditNotice(selectedAdvancePayment.id, note, date).then(resp => {
            if (resp) closeDialog()
        })
    }

    return <>
        <ComponentContainer componentName={f({id: 'advancePayments'})}>
            <ReactiveTable
                swr
                data={advancePaymentsTableData?.rows}
                totalRecords={advancePaymentsTableData?.totalRecords}
                fetchData={fetchData}
                ignoreFilters={['operations']}
                specialFilters={getSpecialFilters()}
                showHeader={true}
                headerButtons={headerButtons}
                exportExcel={exportExcel}
                xlsx={`${moment().format("MM-DD HH-mm")}_${f({id: 'clients'})}`}
                columnTemplate={getColumnTemplate()}
                specialLabels={{
                    amount: "amountWithVat"
                }}
                columnOrder={['clientId', 'amount', 'remainingAmount', 'operations']}
                columnStyle={{
                    operations: {
                        header: {},
                        body: {width: '1px'}
                    }
                }}
                dtProps={{
                    footerColumnGroup: getFooterGroup()
                }}
            />

            <CreateAdvancePayment/>
        </ComponentContainer>

        {selectedAdvancePayment &&
            <CreateCreditNoteForAdvancePayment showDialog={showCreateCreditNoteDialog} closeCb={closeDialog}
                                               invoiceNumber={selectedAdvancePayment.invoiceId.toString()}
                                               createCreditNoteHandler={handleCreateCreditNote}
            />
        }

        {
            (showFilesDialog && selectedAdvancePayment) ?
                <Dialog header={f({id: 'filesForAdvancePayment'})}
                        onHide={() => setShowFilesDialog(false)}
                        visible={showFilesDialog} breakpoints={{'960px': '75vw', '640px': '100vw'}}
                        style={{width: '50vw'}}>
                    <Files selectedRecordId={selectedAdvancePayment.id} allowEdit={false}
                           showDownloadTime={true}
                           dataKey={"/advance-payments/files/all/"}
                           downloadHandler={invoiceService.downloadInvoiceFile}
                    />
                </Dialog> : null
        }
    </>
};

export default AdvancePayments
